import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { api } from "../../../redux/api";
import { useTranslation } from "react-i18next";

function DeleteConfirmationDialog({
  open,
  onClose,
  onRefreshTree,
  urlParams,
  userSpaceChecked,
  serverChecked,
  selectedNodes,
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [userChecked, setUserChecked] = useState(userSpaceChecked);
  const [serverSpaceChecked, setServerSpaceChecked] = useState(serverChecked);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const folderTypeMap = {
    WORKFLOW: "workflows",
    INBOUND: "inbound_templates",
    OUTBOUND: "outbound_templates",
  };

  // Get the item type text for display
  const getItemTypeText = () => {
    if (!selectedNodes || selectedNodes.length === 0) return "items";

    const types = selectedNodes.map((item) => {
      if (item.type === "workflow_group") return "workflow group";
      if (item.type === "template_folder") return "template folder";
      return "template";
    });

    const uniqueTypes = [...new Set(types)];
    if (uniqueTypes.length === 1) {
      return selectedNodes.length > 1 ? `${uniqueTypes[0]}s` : uniqueTypes[0];
    } else {
      return "items";
    }
  };

  const onConfirm = async () => {
    if (userChecked || serverSpaceChecked) {
      setIsDeleting(true);
      try {
        // Transform selectedNodes to match API format
        const selectedItems = selectedNodes.map((selectedNode) => {
          let name = selectedNode.title;
          let path = "";
          let type = "";
          let workflow_only = false;

          // Determine type and path based on node type
          if (selectedNode.type === "workflow_group") {
            type = "workflow_group";
            path = selectedNode.title;
            name = selectedNode.title;
          } else if (selectedNode.type === "template_folder") {
            type = "template_folder";
            path =
              selectedNode?.title === "workflows"
                ? selectedNode.workflowGroup
                : `${selectedNode.workflowGroup}/${selectedNode.title}`;
            name = selectedNode.title;
            workflow_only = selectedNode.workflow_only === "Yes" ? true : false;
          } else {
            // For template files
            type = "file";
            path = `${selectedNode?.workflowGroup}${
              selectedNode?.type === "WORKFLOW"
                ? ""
                : `/${folderTypeMap[selectedNode?.type]}`
            }/${selectedNode?.title}`;
            name = selectedNode.title;
          }

          return {
            name: name,
            path: path,
            type: type,
            workflow_only: workflow_only,
          };
        });

        // Prepare the complete API payload
        const apiPayload = {
          selectedItems: selectedItems,
          userspace: userChecked || false,
          server: serverSpaceChecked || false,
          account: urlParams.account,
          user: urlParams.user,
        };

        const response = await dispatch(
          api.endpoints.deleteTemplatesFromDeployPage.initiate({
            account: urlParams.account,
            data: apiPayload,
          })
        );

        // Handle API response
        if (response.data) {
          const { current_state, data } = response.data;

          if (current_state === "SUCCESS") {
            // Count successful and failed items
            const successCount =
              data.results?.filter((item) => item.status === "success")
                .length || 0;
            const errorCount =
              data.results?.filter((item) => item.status === "error").length ||
              0;

            if (errorCount === 0) {
              // All items deleted successfully
              setSnackbar({
                open: true,
                message: `Successfully deleted ${successCount} item${
                  successCount > 1 ? "s" : ""
                }`,
                severity: "success",
              });
            } else {
              // Some items failed
              const errorMessages = data.errors || [];
              setSnackbar({
                open: true,
                message: `${successCount} item${
                  successCount > 1 ? "s" : ""
                } deleted successfully, ${errorCount} failed: ${errorMessages.join(
                  ", "
                )}`,
                severity: "warning",
              });
            }
          } else {
            // API returned error state
            const errorMessages = data?.errors || ["Unknown error occurred"];
            setSnackbar({
              open: true,
              message: `Failed to delete items: ${errorMessages.join(", ")}`,
              severity: "error",
            });
          }

          // Refresh the tree after API call
          if (onRefreshTree) {
            onRefreshTree();
          }
        } else if (response.error) {
          // Handle API error
          setSnackbar({
            open: true,
            message: `Error deleting items: ${
              response.error.message || "Unknown error"
            }`,
            severity: "error",
          });
        }

        // Close the dialog
        onClose();
      } catch (error) {
        console.error("Error deleting items:", error);
        setSnackbar({
          open: true,
          message: `Error deleting items: ${error.message || "Unknown error"}`,
          severity: "error",
        });
      } finally {
        setIsDeleting(false);
      }
    } else {
      setIsDeleting(false);
      setSnackbar({
        open: true,
        message: `Please select at least one space`,
        severity: "error",
      });
    }
  };

  const handleUserSpaceChange = (event) => {
    setUserChecked(event.target.checked);
  };

  const handleServerChange = (event) => {
    setServerSpaceChecked(event.target.checked);
  };
  
  useEffect(() => {
    setUserChecked(userSpaceChecked);
    setServerSpaceChecked(serverChecked);
  }, [userSpaceChecked, serverChecked]);

  return (
    <>
      <Dialog
        open={open}
        onClose={!isDeleting ? onClose : undefined}
        maxWidth="xs"
        aria-labelledby="delete-dialog-title">
        <DialogTitle id="dialog-header">
         <div className='title'>{t('BOX_CONFIRM')}</div>
        </DialogTitle>
        <DialogContent className='modal-content' dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete <b>{getItemTypeText()}</b>?
          </Typography>

    
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
        <div className="flex justify-start width-100">
            <FormControlLabel
              control={
                <Checkbox
                  checked={userChecked}
                  onChange={handleUserSpaceChange}
                  size="small"
                  disabled={!userSpaceChecked}
                />
              }
              label={<Typography variant="body2">{t('USERSPACE')}</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={serverSpaceChecked}
                  onChange={handleServerChange}
                  size="small"
                  disabled={!serverChecked}
                />
              }
              label={<Typography variant="body2">{t('SERVER')}</Typography>}
            />
          </div>
          <Button
            onClick={onConfirm}
            disabled={isDeleting}
            variant="outlined"
            size="small">
            {isDeleting ? 'DELETING' : t('BTN_YES')}
          </Button>
          <Button
            onClick={onClose}
            disabled={isDeleting}
            variant="outlined"
            size="small">
            No
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            maxWidth: "450px",
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DeleteConfirmationDialog;
